import CardPermissao from "../../../../components/cardPermissao";
import { Box } from "@mui/system";
import useStyles from "./styles";
import { Masonry } from "@mui/lab";
import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import {
  Add,
  Clear,
  PermContactCalendarOutlined,
  Search,
} from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import ModalCreatePermissao from "../../../../components/modalCreatePermissao";
import apiCliente from "../../../../services/apiCliente";
import useRotasDisponiveisCliente from "../../hooksPermissao/useRotasDisponiveisCliente";
import {
  endpointsCliente,
  iconMapCliente,
  iconMapMenuCliente,
} from "./iconsPermissoes";

export default function PermissoesCliente() {
  const theme = useTheme();
  const [buscarPermissao, setBuscarPermissao] = useState("");
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [routesSelect, setRoutesSelect] = useState([]);
  const [allPermissoes, setAllPermissoes] = useState([]);
  const [permissao, setPermissao] = useState({
    codigoPermissao: "",
    descricao: "",
    color: "",
  });

  const styles = useStyles(theme);

  // O ADM do cliente só pode liberar telas que existem para o cliente dele
  const { rotasDisponiveis } = useRotasDisponiveisCliente();

  // A árvore de acessos exibe apenas as rotas disponíveis. Um nó pai continua
  // visível se algum filho estiver disponível (ex.: Dashboard com só uma aba).
  const filtrarRota = useCallback(
    (node) => {
      if (!node?.path) return false;
      if (rotasDisponiveis[node.path]) return true;
      return (node?.children || []).some((child) => filtrarRota(child));
    },
    [rotasDisponiveis],
  );

  useEffect(() => {
    getAllPermissoes();
  }, []);

  const handleOpenModal = () => {
    setOpen(true);
  };

  const getAllPermissoes = async () => {
    try {
      const response = await apiCliente.get("/cliente/find/allPermissoesEdit");

      setAllPermissoes(response?.data);
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Error: Falha ao buscar permissões",
        severity: "error",
      });
    } finally {
    }
  };

  const filteredPermissoes = useMemo(() => {
    const search = buscarPermissao?.toLowerCase();

    if (!search) return allPermissoes;

    // Função recursiva para verificar se uma página ou seus filhos batem com a busca
    const checkPáginas = (paginas) => {
      if (!paginas || !Array.isArray(paginas)) return false;

      return paginas.some((p) => {
        const matchPagina = p.pagina?.toLowerCase().includes(search);
        // Se a página atual bater, ou se algum filho dela bater, retorna true
        return matchPagina || (p.children && checkPáginas(p.children));
      });
    };

    return allPermissoes.filter((permissao) => {
      const matchCodigo = permissao.codigoPermissao
        ?.toLowerCase()
        .includes(search);
      const matchDescricao = permissao.descricao
        ?.toLowerCase()
        .includes(search);

      const matchArrayPermissoes = checkPáginas(permissao.arrayPermissoes);

      return matchCodigo || matchDescricao || matchArrayPermissoes;
    });
  }, [buscarPermissao, allPermissoes]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper
        sx={styles.paper}
        elevation={theme.palette.mode === "dark" ? 0 : 3}
      >
        <Box sx={styles.header}>
          <PermContactCalendarOutlined color="primary" />
          <Typography variant="h5" sx={styles.title}>
            Configuração de Permissões
          </Typography>
        </Box>

        <Box sx={styles.containerHeader}>
          <TextField
            id="input-with-icon-textfield"
            size="small"
            label="Buscar permissão"
            variant="outlined"
            autoComplete="off"
            placeholder="Permissão, descrição, etc..."
            sx={{
              display: "flex",
              flexGrow: 1,
              fontSize: "0.90rem",
              maxWidth: "300px",
            }}
            value={buscarPermissao}
            onChange={(event) => {
              setBuscarPermissao(event?.target?.value?.trimStart());
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: buscarPermissao ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setBuscarPermissao("")}
                    edge="end"
                  >
                    <Clear size={"small"} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button
            onClick={() => handleOpenModal()}
            startIcon={<Add />}
            variant="contained"
          >
            Criar permissão
          </Button>
        </Box>
        {filteredPermissoes?.length > 0 ? (
          <Masonry columns={{ xs: 1, sm: 2, md: 3 }} spacing={2}>
            {filteredPermissoes?.map((permissao) => (
              <Box key={permissao?.id}>
                <CardPermissao
                  permissao={permissao?.codigoPermissao}
                  colorPermissao={permissao?.color}
                  descricao={permissao?.descricao}
                  countUsers={permissao?.usuarios}
                  totalAcessos={permissao?.totalAcessos}
                  arrayPermissoes={permissao?.arrayPermissoes}
                  permissaoObject={permissao}
                  setAllPermissoes={setAllPermissoes}
                  allPermissoes={allPermissoes}
                  setSnackbar={setSnackbar}
                  apiInstance={apiCliente}
                  endpoints={endpointsCliente}
                  iconMap={iconMapCliente}
                  iconMapMenu={iconMapMenuCliente}
                  filtrarRota={filtrarRota}
                  somenteLeitura={permissao?.isGlobal}
                />
              </Box>
            ))}
          </Masonry>
        ) : (
          <Box
            sx={{
              width: "100%",
              p: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: theme.palette.text.secondary }}>
              Nenhuma permissão encontrada
            </Typography>
          </Box>
        )}
      </Paper>
      <ModalCreatePermissao
        setRoutesSelect={setRoutesSelect}
        routesSelect={routesSelect}
        setOpen={setOpen}
        open={open}
        permissao={permissao}
        setPermissao={setPermissao}
        setAllPermissoes={setAllPermissoes}
        setSnackbar={setSnackbar}
        apiInstance={apiCliente}
        endpoints={endpointsCliente}
        iconMap={iconMapMenuCliente}
        filtrarRota={filtrarRota}
      />
      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
